import { z } from "zod";
import { Id, IsoDate, Locale, Page, PageQuery } from "./common.ts";

/**
 * Access requests (ALP-85): sign-up is a request, not an account. A visitor submits the form on /signup; Malfak staff
 * review it in Admin → Requests (contact, notes, approve with a plan, or reject). No `users` row exists until approval:
 * the request keeps the password hash, so an approved requester signs in with the password they chose, and a login
 * attempt while pending/rejected answers `account_pending` / `account_rejected` (403) only when that password matches.
 *
 * The public submit answers the same 202 for a new email, an existing account and a duplicate request (no enumeration).
 */
type Method = "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
export interface AccessRouteDef<P extends z.ZodTypeAny, Q extends z.ZodTypeAny, B extends z.ZodTypeAny, R extends z.ZodTypeAny> {
  method: Method;
  path: string;
  params?: P;
  query?: Q;
  body?: B;
  response: R;
  status?: number;
  summary: string;
}
const route = <P extends z.ZodTypeAny, Q extends z.ZodTypeAny, B extends z.ZodTypeAny, R extends z.ZodTypeAny>(d: AccessRouteDef<P, Q, B, R>) => d;
const Ok = z.object({ ok: z.literal(true) });
const byId = z.object({ id: z.string().uuid() });

// ---------------------------------------------------------------- shared field rules (API + web use the same ones)
/** Names that would be confusing or impersonate staff/system pages. */
export const RESERVED_USERNAMES = [
  "admin", "administrator", "root", "system", "support", "help", "malfak", "staff", "team", "api", "www", "mail", "security",
  "billing", "login", "signup", "settings", "privacy", "terms", "owner", "official", "null", "undefined",
] as const;

/** 3–30 chars: lowercase letters, digits, dot, dash, underscore; starts and ends with a letter or digit; no doubled separators. */
export const USERNAME_RE = /^[a-z0-9](?:[a-z0-9]|[._-](?=[a-z0-9])){1,28}[a-z0-9]$/;
export const Username = z
  .string()
  .trim()
  .toLowerCase()
  .min(3, "too_short")
  .max(30, "too_long")
  .regex(USERNAME_RE, "invalid_chars")
  .refine((u) => !(RESERVED_USERNAMES as readonly string[]).includes(u), "reserved");

/** E.164: "+" then 7–15 digits, no leading zero in the country code. The API also checks the number plan per country. */
export const E164_RE = /^\+[1-9]\d{6,14}$/;
export const PhoneE164 = z.string().trim().regex(E164_RE, "invalid_phone");

export const PASSWORD_MIN = 10;
/**
 * Password strength 0–4 (length, mixed case, digits, symbols, no long repeats). The form's meter shows it; the API
 * refuses anything under 2.
 */
export function passwordScore(pw: string): 0 | 1 | 2 | 3 | 4 {
  if (!pw || pw.length < PASSWORD_MIN) return pw && pw.length >= 6 ? 1 : 0;
  let s = 1;
  const classes = [/[a-z]/, /[A-Z]/, /\d/, /[^A-Za-z0-9]/].filter((r) => r.test(pw)).length;
  if (classes >= 2) s++;
  if (classes >= 3) s++;
  if (pw.length >= 14) s++;
  if (/(.)\1{3,}/.test(pw) || /^(?:password|12345|qwerty|malfak)/i.test(pw)) s = Math.max(1, s - 2);
  return Math.min(4, s) as 0 | 1 | 2 | 3 | 4;
}
export const Password = z
  .string()
  .min(PASSWORD_MIN, "too_short")
  .max(200, "too_long")
  .refine((p) => passwordScore(p) >= 2, "too_weak");

export const HEARD_FROM = ["instagram", "search", "friend", "event", "partner", "other"] as const;
export const HeardFrom = z.enum(HEARD_FROM);

export const AccessRequestInput = z.object({
  email: z.string().trim().toLowerCase().email("invalid_email").max(254),
  username: Username,
  password: Password,
  phone: PhoneE164,
  /** ISO 3166-1 alpha-2 of the picked country (display only; the number itself is E.164). */
  phoneCountry: z.string().trim().length(2).toUpperCase().optional(),
  company: z.string().trim().max(120).optional(),
  reason: z.string().trim().min(20, "too_short").max(2000, "too_long"),
  heardFrom: HeardFrom.optional(),
  locale: Locale.optional(),
  /** Terms + privacy consent: must be true. */
  acceptTerms: z.literal(true, { error: "consent_required" }),
  /** Honeypot (a hidden field people never see): anything in it means a bot; the API answers 202 and stores nothing. */
  website: z.string().max(500).optional(),
  /** Captcha token for when a provider is configured (CAPTCHA_SECRET); ignored otherwise. */
  captchaToken: z.string().max(4096).optional(),
});
/** Always the same answer, whatever happened (new request, existing account, duplicate, honeypot). */
export const AccessRequestAccepted = z.object({ ok: z.literal(true), status: z.literal("received") });
export const UsernameAvailability = z.object({ username: z.string(), available: z.boolean(), reason: z.enum(["taken", "reserved", "invalid"]).nullable() });

// ---------------------------------------------------------------- admin view
export const AccessRequestStatus = z.enum(["new", "contacted", "approved", "rejected"]);
export const ContactChannel = z.enum(["phone", "email", "whatsapp", "meeting", "other"]);
export const AccessRequestNote = z.object({ id: Id, at: IsoDate, byId: z.string().nullable(), byEmail: z.string().nullable(), text: z.string() });
/** The contact log: every status change and every contact attempt, newest first. */
export const AccessRequestEvent = z.object({
  id: Id,
  at: IsoDate,
  kind: z.enum(["submitted", "contacted", "approved", "rejected", "reopened"]),
  byId: z.string().nullable(),
  byEmail: z.string().nullable(),
  channel: ContactChannel.nullable(),
  detail: z.string().nullable(),
});
export const AccessRequestSummary = z.object({
  id: Id,
  email: z.string(),
  username: z.string(),
  phone: z.string(),
  phoneCountry: z.string().nullable(),
  company: z.string().nullable(),
  /** First 160 characters of the reason (the drawer shows all of it). */
  reasonPreview: z.string(),
  heardFrom: HeardFrom.nullable(),
  locale: Locale,
  status: AccessRequestStatus,
  createdAt: IsoDate,
  updatedAt: IsoDate,
  lastContactedAt: IsoDate.nullable(),
  notesCount: z.number(),
});
export const AccessRequestDetail = AccessRequestSummary.extend({
  reason: z.string(),
  consentAt: IsoDate,
  decidedAt: IsoDate.nullable(),
  decidedBy: z.string().nullable(),
  rejectReason: z.string().nullable(),
  planKey: z.enum(["trial", "free", "pro", "business"]).nullable(),
  /** Set once approved: the account and organization created for the requester. */
  userId: Id.nullable(),
  org: z.object({ id: Id, name: z.string() }).nullable(),
  notes: z.array(AccessRequestNote),
  events: z.array(AccessRequestEvent),
});
export const AccessRequestQuery = PageQuery.extend({
  status: AccessRequestStatus.optional(),
  q: z.string().trim().max(200).optional(),
});
export const AccessRequestCounts = z.object({ new: z.number(), contacted: z.number(), approved: z.number(), rejected: z.number(), total: z.number() });

export const ApproveAccessRequest = z.object({
  /** Default Trial (trial end = now + the plan's trial days). */
  planKey: z.enum(["trial", "free", "pro", "business"]).default("trial"),
  /** Organization name; default = the company, else the username. */
  orgName: z.string().trim().min(1).max(120).optional(),
  note: z.string().trim().max(1000).optional(),
});
export const ApproveAccessRequestResult = z.object({
  request: AccessRequestDetail,
  userId: Id,
  org: z.object({ id: Id, name: z.string(), plan: z.enum(["trial", "free", "pro", "business"]) }),
  /** false when the request had already been approved (idempotent repeat). */
  created: z.boolean(),
});
export const RejectAccessRequest = z.object({ reason: z.string().trim().min(3).max(1000) });
export const ContactAccessRequest = z.object({ channel: ContactChannel, detail: z.string().trim().max(1000).optional() });
export const NoteAccessRequest = z.object({ text: z.string().trim().min(1).max(2000) });

/** Platform-level notifications for staff (new access requests, …): not tied to an organization. */
export const AdminNotification = z.object({ id: Id, kind: z.string(), title: z.string(), body: z.string().nullable(), href: z.string().nullable(), readAt: IsoDate.nullable(), createdAt: IsoDate });
export const AdminNotifications = z.object({ items: z.array(AdminNotification), unread: z.number() });

export const accessRequestRoutes = {
  requestAccess: route({ method: "POST", path: "/v1/auth/access-requests", body: AccessRequestInput, response: AccessRequestAccepted, status: 202, summary: "Sign-up = access request (public, rate-limited, honeypot): same 202 for every outcome, staff review it in the admin console" }),
  usernameAvailable: route({ method: "GET", path: "/v1/auth/username-available", query: z.object({ username: z.string().max(60) }), response: UsernameAvailability, summary: "Is this username free (accounts + open requests)? Public, rate-limited" }),

  adminAccessRequests: route({ method: "GET", path: "/v1/admin/access-requests", query: AccessRequestQuery, response: Page(AccessRequestSummary), summary: "Access requests, newest first (filter by status, search email/username/company/phone)" }),
  adminAccessRequestCounts: route({ method: "GET", path: "/v1/admin/access-requests/counts", response: AccessRequestCounts, summary: "Requests per status (tab badge = new)" }),
  adminAccessRequest: route({ method: "GET", path: "/v1/admin/access-requests/:id", params: byId, response: AccessRequestDetail, summary: "One request with notes and the contact log (viewing is audited: personal data)" }),
  adminApproveAccessRequest: route({ method: "POST", path: "/v1/admin/access-requests/:id/approve", params: byId, body: ApproveAccessRequest, response: ApproveAccessRequestResult, summary: "Approve: create the account (the requester's password) + an organization they own on the chosen plan; notify them. Idempotent" }),
  adminRejectAccessRequest: route({ method: "POST", path: "/v1/admin/access-requests/:id/reject", params: byId, body: RejectAccessRequest, response: AccessRequestDetail, summary: "Reject with a reason (sign-in then says 'not approved'). Idempotent" }),
  adminContactAccessRequest: route({ method: "POST", path: "/v1/admin/access-requests/:id/contacted", params: byId, body: ContactAccessRequest, response: AccessRequestDetail, summary: "Log a contact attempt; a new request becomes contacted" }),
  adminNoteAccessRequest: route({ method: "POST", path: "/v1/admin/access-requests/:id/notes", params: byId, body: NoteAccessRequest, response: AccessRequestDetail, summary: "Add an internal note" }),

  adminNotifications: route({ method: "GET", path: "/v1/admin/notifications", query: z.object({ limit: z.coerce.number().int().min(1).max(50).default(20) }), response: AdminNotifications, summary: "My staff notifications (platform-level) + unread count" }),
  adminReadNotifications: route({ method: "POST", path: "/v1/admin/notifications/read", body: z.object({ ids: z.array(Id).max(100).optional() }), response: Ok, summary: "Mark my staff notifications read (all when ids is omitted)" }),
} as const;

export type AccessRequestInput = z.input<typeof AccessRequestInput>;
export type AccessRequestStatus = z.infer<typeof AccessRequestStatus>;
export type AccessRequestSummary = z.infer<typeof AccessRequestSummary>;
export type AccessRequestDetail = z.infer<typeof AccessRequestDetail>;
export type AccessRequestCounts = z.infer<typeof AccessRequestCounts>;
export type AccessRequestEvent = z.infer<typeof AccessRequestEvent>;
export type AccessRequestNote = z.infer<typeof AccessRequestNote>;
export type ContactChannel = z.infer<typeof ContactChannel>;
export type AdminNotification = z.infer<typeof AdminNotification>;
